import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FilePlus2Icon, FolderPlusIcon } from 'lucide-react';
import { Button } from '@monksflow/monks-ui';
import { IdeationWidgetKey } from '@inference.engine/widgets/web';
import { getWidgetStores, type WidgetFullModeProps } from '@/entities/widget';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { notificationService } from '@/lib/services/notification.service';
import { logger } from '@/lib/utils/logger';
import { sanitizeDownloadFileName } from '@/shared/utils/downloadFile';
import { SidePanel, useSidePanelScroll } from '@/shared/ui/SidePanelWrapper';
import type { VersionItem } from '@/shared/ui/VersionSelector';
import { useSaveToProjectEnabled, useServerPdfDownload } from '@/shared/hooks';
import { DownloadIconButton, SaveToProjectPopover } from '@/shared/ui';
import {
  normalizeWidgetData,
  toAdditionalImages,
  toMainImage,
  toSummary,
  type IdeationWidgetData,
  type IdeationWidgetProps,
} from '../model/ideation-widget.types';
import {
  downloadCampaignPdf,
  downloadIdeaPdf,
  saveIdeationCampaignArtifact,
  saveIdeationIdeaArtifact,
} from '../api/ideation-artifact.api';
import { IdeaDetailPanel } from './components/IdeaDetailPanel';
import { IdeaListPanel } from './components/IdeaListPanel';
import { useTranslation } from 'react-i18next';

// Helper functions for version ID handling
const getIdeaVersionId = (index: number) => `idea-${index}`;
const parseIdeaIndex = (versionId: string): number | null => {
  const match = versionId.match(/^idea-(\d+)$/);
  return match ? parseInt(match[1], 10) : null;
};

export const IdeationWidget = (props: WidgetFullModeProps<IdeationWidgetProps>) => {
  const { t } = useTranslation('contentTest');

  const rawData = props.data;
  const briefDeps = props.briefDeps;
  const readOnly = props.readOnly ?? false;
  // Library preview tells us explicitly which view to open (a saved idea → its
  // detail, a saved campaign → the overview); the live chat leaves it undefined.
  const previewInitialIdeaIndex = props.previewInitialIdeaIndex ?? null;

  // Normalize V1 data to V2 format for backward compatibility
  const initialData = useMemo(() => normalizeWidgetData(rawData), [rawData]);
  const [data, setData] = useState<IdeationWidgetData>(initialData);

  const [selectedIdeaIndex, setSelectedIdeaIndex] = useState<number | null>(
    () => previewInitialIdeaIndex,
  );

  // Store + chats access via the widget binding seam.
  // See `entities/widget/lib/widget-store-bindings`. Production wiring at
  // app boot supplies the real `@/app/store/widget` + `@/features/chats`
  // modules; tests stub `getWidgetStores()` at the seam.
  const { widgetStore, chatStore } = getWidgetStores();
  const { useChatStore, useChatStoreInput } = chatStore;
  const { useWidgetPanel, useWidgetVersions, useCreateArtifactVersion } = widgetStore;

  const { widget } = useWidgetPanel();
  const agentId = widget?.agentName ?? '';
  const artifactId = (widget?.artifactId as string | undefined) ?? '';
  const agentsWidgets = useWidgetVersions(agentId);
  const createArtifactVersionResult = useCreateArtifactVersion() as {
    mutateAsync: (params: Record<string, unknown>) => Promise<unknown>;
    isPending: boolean;
  };
  const { mutateAsync: createArtifactVersion, isPending } = createArtifactVersionResult;
  const { setOptions } = useChatStoreInput();
  const { scrollToTop } = useSidePanelScroll();

  const saveEnabled = useSaveToProjectEnabled();
  const workspaceId = useCurrentWorkspaceId();

  // Convert flat fields to component-friendly structures
  const summary = useMemo(() => toSummary(data), [data]);
  const mainImage = useMemo(() => toMainImage(data), [data]);
  const additionalImages = useMemo(() => toAdditionalImages(data), [data]);

  const setIdeaWithScroll = useCallback(
    (ideaIndex: number | null) => {
      setSelectedIdeaIndex(ideaIndex);
      scrollToTop();
    },
    [scrollToTop],
  );

  // Sync local state when widget data changes (e.g., version switch).
  useEffect(() => {
    setData(initialData);
    setIdeaWithScroll(previewInitialIdeaIndex);
  }, [initialData, previewInitialIdeaIndex, setIdeaWithScroll]);

  // Use ref to capture setOptions for cleanup to avoid stale closure issues
  const setOptionsRef = useRef(setOptions);
  setOptionsRef.current = setOptions;

  const pdf = useServerPdfDownload({
    preparing: t('ideationWidget.preparingYourDownload'),
    failed: t('ideationWidget.failedToDownload'),
  });
  // A stale index exports the overview, never the wrong idea.
  const downloadIdeaIndex =
    selectedIdeaIndex !== null && selectedIdeaIndex < data.ideas.length ? selectedIdeaIndex : null;
  // eslint-disable-next-line i18next/no-literal-string -- in-flight key, never rendered
  const downloadKey = downloadIdeaIndex !== null ? `idea-${downloadIdeaIndex}` : 'overview';
  const isDownloading = pdf.isDownloading(downloadKey);
  const { download: downloadPdf } = pdf;

  // An open idea exports that idea's detail; the overview exports the campaign.
  const handleDownload = useCallback(async () => {
    if (!artifactId || !workspaceId) return;
    const name =
      downloadIdeaIndex !== null
        ? `Idea ${downloadIdeaIndex + 1} - ${data.ideas[downloadIdeaIndex].title}`
        : `Ideation Overview - ${data.title}`;
    await downloadPdf({
      key: downloadKey,
      fetchBlob: () =>
        downloadIdeaIndex !== null
          ? downloadIdeaPdf(artifactId, { workspaceId, itemIndex: downloadIdeaIndex })
          : downloadCampaignPdf(artifactId, { workspaceId }),
      // eslint-disable-next-line i18next/no-literal-string -- download filename segment
      fileName: `${sanitizeDownloadFileName(name, 'ideation')}.pdf`,
      analytics: {
        document_type: 'ideation',
        scope: downloadIdeaIndex !== null ? 'item' : 'overview',
      },
    });
  }, [
    artifactId,
    workspaceId,
    downloadIdeaIndex,
    downloadKey,
    data.ideas,
    data.title,
    downloadPdf,
  ]);

  // Cleanup ideaId from options when widget unmounts
  useEffect(() => {
    return () => {
      setOptionsRef.current((prev) => ({
        ...prev,
        ideaId: undefined,
      }));
    };
  }, []);

  // Convert ideas to version items for the version selector
  // Validate and get timestamp, fallback to current time if invalid
  const createdAtTimestamp = new Date(data.createdAt).getTime();
  const validTimestamp = isNaN(createdAtTimestamp) ? Date.now() : createdAtTimestamp;

  const versionItems = data.ideas.map((idea, index) => ({
    id: getIdeaVersionId(index),
    title: idea.title,
    timestamp: validTimestamp,
    version: index + 1,
    agentId: '',
    agentLabel: '',
  }));

  const handleVersionSelect = useCallback(
    (version: VersionItem) => {
      const ideaIndex = parseIdeaIndex(version.id);
      if (ideaIndex !== null && ideaIndex >= 0 && ideaIndex < data.ideas.length) {
        setIdeaWithScroll(ideaIndex);
      }
    },
    [data.ideas.length, setIdeaWithScroll],
  );

  const handleCloseDetailPanel = useCallback(() => {
    setOptions((prev) => ({
      ...prev,
      ideaId: undefined,
    }));
    setIdeaWithScroll(null);
  }, [setOptions, setIdeaWithScroll]);

  const handleIdeaChange = useCallback(
    (ideaIndex: number) => {
      setIdeaWithScroll(ideaIndex);

      // Update chat options with the new ideaId
      const idea = data.ideas[ideaIndex];
      setOptions((prev) => ({
        ...prev,
        ideaId: idea.id,
      }));
    },
    [data.ideas, setOptions, setIdeaWithScroll],
  );

  const hasSelectedIdea = selectedIdeaIndex !== null && selectedIdeaIndex < data.ideas.length;

  // Start Brief: same gate as save; the campaign/idea is not seeded into the brief.
  const showBriefAction = saveEnabled && !readOnly;
  const briefAction =
    showBriefAction && workspaceId
      ? [
          {
            type: 'custom' as const,
            tooltip: t('ideationWidget.startABrief'),
            component: (
              <briefDeps.StartBrief>
                <Button aria-label={t('ideationWidget.startABrief')} size="icon-sm" variant="ghost">
                  <FilePlus2Icon className="size-5" />
                </Button>
              </briefDeps.StartBrief>
            ),
          },
        ]
      : [];

  // Save to Project: the overview saves the campaign; an open idea saves that idea.
  const showSaveAction = saveEnabled && !readOnly && !!artifactId && !!workspaceId;
  // The selected idea index, or null on the overview (`!== null` narrows to number below).
  const ideaIndexForSave = hasSelectedIdea ? selectedIdeaIndex : null;
  const saveAction =
    showSaveAction && workspaceId && artifactId
      ? [
          {
            type: 'custom' as const,
            tooltip:
              ideaIndexForSave !== null
                ? t('ideationWidget.saveIdeaToProject')
                : t('ideationWidget.saveCampaignToProject'),
            component: (
              <SaveToProjectPopover
                workspaceId={workspaceId}
                resourceId={
                  ideaIndexForSave !== null ? `${artifactId}:${ideaIndexForSave}` : artifactId
                }
                onMintArtifact={
                  ideaIndexForSave !== null
                    ? () => saveIdeationIdeaArtifact(workspaceId, artifactId, ideaIndexForSave)
                    : () => saveIdeationCampaignArtifact(workspaceId, artifactId)
                }
              >
                <Button
                  aria-label={
                    ideaIndexForSave !== null
                      ? t('ideationWidget.saveIdeaToProject')
                      : t('ideationWidget.saveCampaignToProject')
                  }
                  size="icon-sm"
                  variant="ghost"
                  className="mr-2"
                >
                  <FolderPlusIcon className="size-5" />
                </Button>
              </SaveToProjectPopover>
            ),
          },
        ]
      : [];

  const headerConfig = {
    ...(!hasSelectedIdea && {
      chip: {
        label: t('ideationWidget.creativeOverview'),
        color: 'lightBlue' as const,
      },
    }),
    versionConfig:
      selectedIdeaIndex !== null
        ? {
            currentVersion: versionItems[selectedIdeaIndex],
            versions: versionItems.filter((_, idx) => idx !== selectedIdeaIndex),
            onVersionSelect: handleVersionSelect,
          }
        : agentsWidgets,
    actions: [
      // The PDF is rendered server-side per workspace and artifact.
      ...(workspaceId && artifactId
        ? [
            {
              type: 'custom' as const,
              component: (
                <DownloadIconButton
                  label={t('ideationWidget.downloadReport')}
                  onDownload={handleDownload}
                  isDownloading={isDownloading}
                  tooltipSide="bottom"
                />
              ),
            },
          ]
        : []),
      ...briefAction,
      ...saveAction,
    ],
    ...(hasSelectedIdea && {
      backButton: {
        label: t('ideationWidget.back'),
        onClick: handleCloseDetailPanel,
      },
    }),
  };

  const handleSaveDescription = useCallback(
    (ideaIndex: number, newDescription: string) => {
      const newIdeas = data.ideas.map((idea, idx) => {
        if (idx !== ideaIndex) return idea;
        return {
          ...idea,
          description: newDescription,
        };
      });

      const updatedData: IdeationWidgetData = {
        ...data,
        ideas: newIdeas,
        updatedAt: new Date().toISOString(),
      };

      const chatId = (useChatStore.getState() as { chatId?: string }).chatId;
      const exchangeId = widget?.exchangeId as string | undefined;

      if (!chatId || !exchangeId) {
        notificationService.showError(t('ideationWidget.unableToSaveSessionInformation'));
        return;
      }

      createArtifactVersion({
        chatId,
        exchangeId,
        key: IdeationWidgetKey,
        // eslint-disable-next-line i18next/no-literal-string -- persisted artifact name that `apps/api` writes with the same literal; getArtifactDisplayName translates it on read
        name: 'Ideation Widget',
        // eslint-disable-next-line i18next/no-literal-string -- artifact kind wire enum value
        kind: 'ideation',
        data: updatedData,
      })
        .then(() => {
          setData(updatedData);
          notificationService.showSuccess(t('ideationWidget.descriptionUpdated'));
        })
        .catch((err: unknown) => {
          logger.error('Failed to create ideation widget v2 artifact version', err);
          notificationService.showError(t('ideationWidget.failedToSaveDescription'));
        });
    },
    [data, widget, createArtifactVersion, useChatStore, t],
  );

  // Read-only Library preview: the modal owns the chrome, so render without the
  // SidePanel header. Single saved idea → its detail; campaign → overview with a
  // Back button on drill-in.
  const isSingleIdeaPreview = previewInitialIdeaIndex !== null;
  if (readOnly) {
    if (hasSelectedIdea) {
      const ideaDetail = (
        <IdeaDetailPanel
          idea={data.ideas[selectedIdeaIndex]}
          ideaIndex={selectedIdeaIndex}
          summary={summary}
          benchmarkReferences={data.references}
          onBack={handleCloseDetailPanel}
          onDescriptionSave={handleSaveDescription}
          isSaving={isPending}
        />
      );
      return isSingleIdeaPreview ? (
        <div className="h-full overflow-y-auto">{ideaDetail}</div>
      ) : (
        <SidePanel
          headerConfig={{
            backButton: { label: t('ideationWidget.back'), onClick: handleCloseDetailPanel },
          }}
        >
          {ideaDetail}
        </SidePanel>
      );
    }
    return (
      <div className="h-full overflow-y-auto">
        <IdeaListPanel
          ideas={data.ideas}
          summary={summary}
          benchmarkReferences={data.references}
          campaignTitle={data.title}
          mainImage={mainImage}
          additionalImages={additionalImages}
          onIdeaSelect={handleIdeaChange}
        />
      </div>
    );
  }

  return (
    <SidePanel headerConfig={headerConfig}>
      {!hasSelectedIdea && (
        <IdeaListPanel
          ideas={data.ideas}
          summary={summary}
          benchmarkReferences={data.references}
          campaignTitle={data.title}
          mainImage={mainImage}
          additionalImages={additionalImages}
          onIdeaSelect={handleIdeaChange}
        />
      )}

      {/* If an idea is selected, show the detail panel*/}
      {hasSelectedIdea && (
        <IdeaDetailPanel
          idea={data.ideas[selectedIdeaIndex]}
          ideaIndex={selectedIdeaIndex}
          summary={summary}
          benchmarkReferences={data.references}
          onBack={handleCloseDetailPanel}
          onDescriptionSave={handleSaveDescription}
          isSaving={isPending}
          versionConfig={{
            currentVersion: versionItems[selectedIdeaIndex],
            versions: versionItems.filter((_, idx) => idx !== selectedIdeaIndex),
            onVersionSelect: handleVersionSelect,
          }}
        />
      )}
    </SidePanel>
  );
};
